import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Dropdown } from '@factorialco/f0-react/dist/experimental'
import {
  ArrowUp,
  BookOpen,
  CheckCircleLine,
  FacePositive,
  Graph,
  Money,
  PalmTree,
  Timer,
} from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import 'modules/meetings/lib/utils/index.styles.css'

interface MessageInputProps {
  isSending: boolean
  onSend: (message: string) => void
  disabled?: boolean
  placeholder?: string
  clearChat?: () => void
  sources?: string
}

export const MessageInput = ({
  disabled = false,
  onSend,
  isSending,
  placeholder,
  clearChat,
  sources,
}: MessageInputProps) => {
  const [message, setMessage] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [gradientAngle, setGradientAngle] = useState(0)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const animationRef = useRef<number>()

  const parsedSources = useMemo(() => {
    if (!sources) return []

    try {
      return JSON.parse(sources)
    } catch (error) {
      return []
    }
  }, [sources])

  const handleSend = () => {
    onSend(message)
    setMessage('')
  }

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    // If textarea is empty, set to minimum height
    if (message.trim() === '') {
      textarea.style.height = '24px'
      textarea.style.overflowY = 'hidden'
      return
    }

    textarea.style.height = 'auto'

    const lineHeight = 24
    const maxRows = 4
    const maxHeight = lineHeight * maxRows

    const newHeight = Math.max(lineHeight, Math.min(textarea.scrollHeight, maxHeight))
    textarea.style.height = `${newHeight}px`
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden'
  }, [message])

  useEffect(() => {
    adjustTextareaHeight()
  }, [adjustTextareaHeight, message])

  // Animate gradient angle when focused
  useEffect(() => {
    if (isFocused) {
      const animate = () => {
        setGradientAngle((prev) => (prev + 1) % 360)
        animationRef.current = requestAnimationFrame(animate)
      }
      animationRef.current = requestAnimationFrame(animate)
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [isFocused])

  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = () => {
    setIsFocused(false)
  }

  const getModuleName = (module: string) => {
    switch (module) {
      case 'performance':
        return {
          label: i18n.t('meetings.meeting.chat.sources.performance'),
          icon: Graph,
        }
      case 'salary':
        return {
          label: i18n.t('meetings.meeting.chat.sources.salary'),
          icon: Money,
        }
      case 'tasks':
        return {
          label: i18n.t('meetings.meeting.chat.sources.tasks'),
          icon: CheckCircleLine,
        }
      case 'training':
        return {
          label: i18n.t('meetings.meeting.chat.sources.training'),
          icon: BookOpen,
        }
      case 'timesheet':
        return {
          label: i18n.t('meetings.meeting.chat.sources.timesheet'),
          icon: Timer,
        }
      case 'mood':
        return {
          label: i18n.t('meetings.meeting.chat.sources.mood'),
          icon: FacePositive,
        }
      case 'timeoff':
        return {
          label: i18n.t('meetings.meeting.chat.sources.timeoff'),
          icon: PalmTree,
        }
    }
  }

  const handleNavigate = (link: string) => {
    window.open(link, '_blank', 'noopener,noreferrer')
  }

  return (
    <Box position="relative">
      <Box
        position="absolute"
        style={{
          top: '-30px',
          left: '0px',
        }}
        gap="s6"
        flexDirection="row"
      >
        {clearChat && (
          <Box background="grey000White" borderRadius={{ all: 'abs008' }}>
            <F0Button
              label={i18n.t('meetings.meeting.chat.clear_chat')}
              onClick={clearChat}
              size="sm"
              variant="outline"
            />
          </Box>
        )}
        {parsedSources.length > 0 && (
          <Box background="grey000White" borderRadius={{ all: 'abs008' }}>
            <Dropdown
              size="sm"
              items={parsedSources.map(
                (source: { description: string; module: string; link: string }) => ({
                  description: source.description,
                  onClick: () => handleNavigate(source.link),
                  ...getModuleName(source.module),
                })
              )}
            >
              <F0Button
                label={i18n.t('meetings.meeting.chat.sources.button')}
                size="sm"
                variant="neutral"
              />
            </Dropdown>
          </Box>
        )}
      </Box>
      <Box
        width="full"
        height="full"
        borderRadius={{
          all: 'abs012',
        }}
        position="absolute"
        style={{
          pointerEvents: 'none',
          inset: '0',
          zIndex: '1',
          opacity: isFocused ? '0.5' : '0',
          filter: 'blur(3px)',
          transform: isFocused ? 'scale(1)' : 'scale(0.95)',
          background: `conic-gradient(from ${gradientAngle}deg, #E55619, #A1ADE5, #E51943, #E55619)`,
          transition: 'all 300ms ease-in-out',
          transitionDelay: '100ms',
        }}
      />
      <Box
        border={{
          all: {
            width: 's1',
            style: 'solid',
            color: 'grey400',
          },
        }}
        borderRadius={{
          all: 'abs012',
        }}
        padding="s12"
        gap="s12"
        background="grey000White"
        style={{
          zIndex: '2',
        }}
      >
        <textarea
          className={'utils_hiddenScrollBar'}
          ref={textareaRef}
          placeholder={placeholder || i18n.t('meetings.meeting.chat.write_message')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && message.trim().length > 0) {
              e.preventDefault()
              handleSend()
            }
          }}
          style={{
            width: '100%',
            resize: 'none',
            border: 'none',
            outline: 'none',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            lineHeight: '1.5',
          }}
          rows={1}
          disabled={disabled}
        />
        <Box
          justifyContent={clearChat ? 'spaceBetween' : 'flexEnd'}
          alignItems="center"
          flexDirection="row"
        >
          <F0Button
            label="send"
            onClick={handleSend}
            loading={isSending}
            size="md"
            hideLabel
            variant={disabled || message.trim().length === 0 ? 'neutral' : 'default'}
            icon={ArrowUp}
            disabled={disabled || message.trim().length === 0 || isSending}
          />
        </Box>
      </Box>
    </Box>
  )
}
